import {ChangeDetectionStrategy, Component, DestroyRef, OnInit} from '@angular/core'
import {ArticlePreview, TopicListItem} from '../../../../core/content/types/article.types'
import {LinkService} from '../../../../core/i18n/link.service'
import {Observable, of, Subject} from 'rxjs'
import {PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {TranslocoService} from '@ngneat/transloco'
import {pickProperty, QueryWithEffects, queryWithEffects} from '../../../../core/graphql/graphql-utils'
import {debounceTime, map} from 'rxjs/operators'
import {Language} from '../../../../core/i18n/language'
import {ArticleContentService} from '../../../../core/content/services/article-content.service'
import {ArticleOverviewPage_SearchArticlesGql} from '../../../../../__generated__/types'
import {FilterOption} from '../../../../craft/filter/filter.types'
import {FormControl} from '@angular/forms'
import {OptableLayout} from '../../../../core/components/layout/optable-layout.interface'
import {parseArticlePreview} from '../../../../core/content/serde/article-page.serde'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'

@Component({
  templateUrl: './article-overview-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArticleOverviewPageComponent extends PageComponent implements OptableLayout, OnInit {
  optOutOfLayout = true

  readonly topicsQuery: QueryWithEffects<TopicListItem[]>
  readonly topics$: Observable<TopicListItem[]>

  // multiple of 2 and 3 to fill the rows + 1 for the heading article
  readonly initialPageSize = 19
  readonly isLoadingMore$ = new Subject<boolean>()
  readonly articles$: Observable<{total: number; items: ArticlePreview[]}>
  private readonly searchArticlesQuery = this.searchArticlesGql.watch({page: {offset: 0, limit: this.initialPageSize}})

  readonly Language = Language

  topicList$: Observable<FilterOption<string>[]> = this.contentService
    .getTopicList()
    .pipe(map(topics => topics.map(t => ({value: t.slug, name: t.breadcrumbTitle || t.title}))))
  authorList$: Observable<FilterOption<string>[]> = this.contentService
    .getAllArticleAuthorNames()
    .pipe(map(t => t.map(v => ({value: v, name: v}))))

  readonly searchControl = new FormControl('')

  constructor(
    private readonly contentService: ArticleContentService,
    private readonly searchArticlesGql: ArticleOverviewPage_SearchArticlesGql,
    readonly linkService: LinkService,
    readonly transloco: TranslocoService,
    private readonly destroyRef: DestroyRef,
  ) {
    super()
    this.topicsQuery = queryWithEffects(this.contentService.getTopicList())
    this.topics$ = this.topicsQuery.data$
    this.articles$ = this.searchArticlesQuery.valueChanges.pipe(
      map(res => pickProperty(res.data, 'searchArticles')),
      map(({total, items}) => {
        return {
          total,
          items: items.map(a => parseArticlePreview(a, this.linkService)),
        }
      }),
    )
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.transloco.selectTranslateObject('article.overview').pipe(
      map(({heading, subheading}) => ({
        title: heading,
        description: subheading,
      })),
    )
  }
  getPageAvailableLanguages(): Observable<Language[]> {
    return of(Object.values(Language))
  }

  ngOnInit() {
    this.observeSearchFilter()
  }

  loadMoreArticles(offset: number) {
    const limit = 18 // load a multiple of 2 and 3 to fill the rows regardless of screen resolution (grid is 1, 2 or 3 rows)
    this.isLoadingMore$.next(true)
    // in order to have pagination handled by apollo (using fetchMore), a type policy is defined in apollo-factory.ts,
    // to let apollo know how to merge the new data with the existing cache
    this.searchArticlesQuery.fetchMore({variables: {page: {limit, offset}}}).then(() => {
      this.isLoadingMore$.next(false)
    })
  }

  private observeSearchFilter() {
    this.searchControl.valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef)).subscribe(search => {
      this.searchArticlesQuery.refetch({
        searchText: search || undefined,
        page: {offset: 0, limit: this.initialPageSize},
      })
    })
  }

  authorFilterChanged(event?: FilterOption<unknown>) {
    const authorName = typeof event?.value === 'string' ? event.value : ''
    this.searchArticlesQuery.refetch({authorName, page: {offset: 0, limit: this.initialPageSize}})
  }

  topicFilterChanged(event?: FilterOption<unknown>) {
    const topic = typeof event?.value === 'string' ? event.value : ''
    this.searchArticlesQuery.refetch({topic, page: {offset: 0, limit: this.initialPageSize}})
  }
}
